import * as React from "react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { ErrorState, useLoadingTimeout } from "@shared/ui/AsyncState";
import { Deferred } from "@shared/ui/Deferred";
import { Logger } from "@shared/lib/logger";

import ProtectedRoute from "./ProtectedRoute";
import {
  getRecoverableRouteErrorAction,
  isRouteChunkLoadError,
} from "./routeGuards";

type RouteElementOptions = {
  delay?: number;
  fallback?: React.ReactNode;
};

type ProtectedRouteElementOptions = RouteElementOptions & {
  requirePerms?: string[];
  requireRoles?: string[];
  showTrialBanner?: boolean;
  allowOfflineSession?: boolean;
};

type RouteErrorBoundaryState = {
  error: Error | null;
  info: React.ErrorInfo | null;
};

type RouteErrorBoundaryProps = {
  children: React.ReactNode;
  resetKey: string;
};

class RouteErrorBoundaryInner extends React.Component<
  RouteErrorBoundaryProps,
  RouteErrorBoundaryState
> {
  state: RouteErrorBoundaryState = { error: null, info: null };

  static getDerivedStateFromError(error: Error): RouteErrorBoundaryState {
    return { error, info: null };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    this.setState({ info });
    Logger.error("Admin route boundary caught an error:", error, info);
  }

  componentDidUpdate(prevProps: RouteErrorBoundaryProps): void {
    if (prevProps.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null, info: null });
    }
  }

  reset = () => {
    this.setState({ error: null, info: null });
  };

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <AdminRouteErrorFallback
        error={this.state.error}
        info={this.state.info}
        onReset={this.reset}
      />
    );
  }
}

function AdminRouteErrorFallback({
  error,
  info,
  onReset,
}: {
  error: Error;
  info: React.ErrorInfo | null;
  onReset: () => void;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation("common");
  const { location } = useRouterState({
    select: (s) => ({ location: s.location }),
  });
  const recoverableAction = getRecoverableRouteErrorAction(error, location);

  React.useEffect(() => {
    if (!recoverableAction) return;

    if (recoverableAction.type === "login") {
      navigate({
        to: "/login",
        search: recoverableAction.search as any,
        replace: true,
      } as any);
      return;
    }

    navigate({ to: recoverableAction.to, replace: true } as any);
  }, [navigate, recoverableAction]);

  const copyDetails = React.useCallback(async () => {
    const payload = [
      `${error.name}: ${error.message}`,
      error.stack || "",
      info?.componentStack || "",
    ]
      .filter(Boolean)
      .join("\n\n");
    try {
      await navigator.clipboard.writeText(payload);
    } catch {
      // ignore clipboard failures; the visible error state still gives recovery actions
    }
  }, [error, info]);

  if (recoverableAction) {
    return (
      <ErrorState
        title={t("route.restoringTitle")}
        message={
          recoverableAction.type === "login"
            ? t("route.signInMessage")
            : t("route.storeRestoreMessage")
        }
      />
    );
  }

  const chunkLoadError = isRouteChunkLoadError(error);
  const isProd = import.meta.env.PROD;

  return (
    <div className="space-y-3">
      <ErrorState
        title={t("route.unableTitle")}
        message={
          chunkLoadError ? t("route.chunkMessage") : t("route.genericMessage")
        }
        retryLabel={t("route.reloadPage")}
        onRetry={() => {
          if (typeof window !== "undefined") window.location.reload();
        }}
      />
      {!isProd ? (
        <pre className="max-h-60 overflow-auto rounded-md border bg-background p-3 text-xs text-destructive">
          {error.stack || error.message}
        </pre>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={onReset}
          className="rounded-md border px-3 py-1.5 text-sm hover:bg-primary/10"
        >
          {t("route.retryRoute")}
        </button>
        <button
          type="button"
          onClick={copyDetails}
          className="rounded-md border px-3 py-1.5 text-sm hover:bg-primary/10"
        >
          {t("route.copyDetails")}
        </button>
      </div>
    </div>
  );
}

function RouteErrorBoundary({ children }: { children: React.ReactNode }) {
  const { location } = useRouterState({
    select: (s) => ({ location: s.location }),
  });
  const resetKey = `${location.pathname}${location.searchStr ?? ""}${location.hash ?? ""}`;

  return (
    <RouteErrorBoundaryInner resetKey={resetKey}>
      {children}
    </RouteErrorBoundaryInner>
  );
}

function RouteFallback({ fallback }: { fallback?: React.ReactNode }) {
  const timedOut = useLoadingTimeout(true);
  const { t } = useTranslation("common");

  if (timedOut) {
    return (
      <ErrorState
        title={t("route.timeoutTitle")}
        message={t("route.timeoutMessage")}
        retryLabel={t("route.reloadPage")}
        onRetry={() => {
          if (typeof window !== "undefined") window.location.reload();
        }}
      />
    );
  }

  return <>{fallback || <div>{t("route.loading")}</div>}</>;
}

export function RouteSuspense({
  children,
  delay = 250,
  fallback,
}: React.PropsWithChildren<RouteElementOptions>) {
  return (
    <React.Suspense
      fallback={
        <Deferred delay={delay}>
          <RouteFallback fallback={fallback} />
        </Deferred>
      }
    >
      {children}
    </React.Suspense>
  );
}

export function renderRoute(
  Component: React.ElementType,
  options: RouteElementOptions = {},
) {
  const { delay, fallback } = options;

  return (
    <RouteErrorBoundary>
      <RouteSuspense delay={delay} fallback={fallback}>
        {React.createElement(Component)}
      </RouteSuspense>
    </RouteErrorBoundary>
  );
}

export function renderProtectedRoute(
  Component: React.ElementType,
  options: ProtectedRouteElementOptions = {},
) {
  const {
    allowOfflineSession,
    delay,
    fallback,
    requirePerms,
    requireRoles,
    showTrialBanner,
  } = options;

  return (
    <RouteErrorBoundary>
      <RouteSuspense delay={delay} fallback={fallback}>
        <ProtectedRoute
          requirePerms={requirePerms}
          requireRoles={requireRoles}
          showTrialBanner={showTrialBanner}
          allowOfflineSession={allowOfflineSession}
        >
          {React.createElement(Component)}
        </ProtectedRoute>
      </RouteSuspense>
    </RouteErrorBoundary>
  );
}
